import React, { useEffect, useState } from 'react';
import {
  AlertDialog,
  AlertDialogBody,
  AlertDialogContent,
  AlertDialogFooter,
  AlertDialogHeader,
  AlertDialogOverlay,
  Box,
  Button,
  FormControl,
  HStack,
  Input,
  Menu,
  MenuButton,
  MenuList,
  useColorModeValue,
  VStack,
} from '@cardboard-ui/react';
import { t } from '@lingui/macro';
import {
  useAttrs,
  useChainedCommands,
  useCommands,
  useCurrentSelection,
  useExtension,
} from '@remirror/react';
import { PrimaryButton, SecondaryButton } from 'components/buttons';
import { DropdownIcon, LinkIcon, LinkSlashIcon } from 'components/icons';
import { ShortcutHandlerProps } from 'remirror/extensions';
import { sanitizeUrl } from '@braintree/sanitize-url';
import { FloatingToolbarMenuItem } from './FloatingToolbarMenuItem';
import { CustomLinkExtension } from '../CustomLinkExtension';
import { OverlayProps } from 'components/TaskOverlay';

function useLinkShortcut() {
  const [linkShortcut, setLinkShortcut] = useState<
    ShortcutHandlerProps | undefined
  >();
  const [isEditing, setIsEditing] = useState(false);

  useExtension(
    CustomLinkExtension,
    ({ addHandler }) =>
      addHandler('onShortcut', (props) => {
        if (!isEditing) {
          setIsEditing(true);
        }

        return setLinkShortcut(props);
      }),
    [isEditing],
  );

  return { linkShortcut, isEditing, setIsEditing };
}

interface LinkToolProps {
  editable: boolean;
  onOpenLinkTool: () => void;
}

export const LinkTool = ({ editable, onOpenLinkTool }: LinkToolProps) => {
  const chain = useChainedCommands();
  const commands = useCommands();
  const [linkValue, setLinkValue] = useState<string>('');
  const url = (useAttrs().link()?.href as string) ?? '';
  const { empty } = useCurrentSelection();
  const { isEditing } = useLinkShortcut();

  useEffect(() => {
    setLinkValue(url);
  }, [url]);

  useExtension(
    CustomLinkExtension,
    ({ addHandler }) =>
      addHandler('onClick', (ev, data) => {
        if (editable) {
          if (empty) {
            chain.selectLink().run();
          }
          ev.preventDefault();
        } else {
          window.__handleLinkClick &&
            window.__handleLinkClick(ev as MouseEvent);
        }

        return false;
      }),
    [isEditing],
  );

  return (
    <>
      <Menu>
        <MenuButton
          as={Button}
          rightIcon={
            <Box marginLeft={2}>
              <DropdownIcon />
            </Box>
          }
          size="xs"
          variant="ghost"
          fontWeight="normal"
        >
          {t`Link`}
        </MenuButton>
        <MenuList
          shadow="lg"
          py="4"
          color={useColorModeValue('gray.600', 'gray.200')}
          px="3"
          minW="auto"
        >
          <VStack align="flex-start">
            <FloatingToolbarMenuItem
              title={linkValue ? t`Edit Link` : t`Add Link`}
              icon={<LinkIcon />}
              onClick={() => {
                onOpenLinkTool();
              }}
            />
            {!!linkValue && (
              <FloatingToolbarMenuItem
                title={t`Remove Link`}
                icon={<LinkSlashIcon />}
                onClick={() => {
                  commands.removeLink();
                }}
              />
            )}
          </VStack>
        </MenuList>
      </Menu>
    </>
  );
};

export function LinkToolModal({ isOpen, onClose }: OverlayProps) {
  const cancelLinkRef = React.useRef(null);
  const [linkValue, setLinkValue] = useState<string>('');
  const url = (useAttrs().link()?.href as string) ?? '';
  const chain = useChainedCommands();
  const { to } = useCurrentSelection();
  const { linkShortcut } = useLinkShortcut();
  const range = linkShortcut ?? undefined;

  useEffect(() => {
    setLinkValue(url);
  }, [url]);

  return (
    <AlertDialog
      isOpen={isOpen}
      onClose={onClose}
      leastDestructiveRef={cancelLinkRef}
    >
      <AlertDialogOverlay>
        <AlertDialogContent>
          <AlertDialogHeader fontSize="lg" fontWeight="bold">
            {t`Insert link`}
          </AlertDialogHeader>
          <AlertDialogBody>
            <Box>
              <FormControl>
                <Input
                  type="text"
                  value={linkValue}
                  onChange={({ target: { value } }) => setLinkValue(value)}
                  isInvalid={sanitizeUrl(linkValue) === 'about:blank'}
                />
              </FormControl>
            </Box>
          </AlertDialogBody>
          <AlertDialogFooter>
            <HStack spacing={2}>
              <div ref={cancelLinkRef}>
                <SecondaryButton onClick={() => onClose()}>
                  {t`Cancel`}
                </SecondaryButton>
              </div>
              <PrimaryButton
                onClick={() => {
                  onClose();
                  if (!linkValue) {
                    chain.removeLink();
                  } else {
                    chain.updateLink({ href: linkValue, auto: false }, range);
                    chain.focus(range?.to ?? to).run();
                  }
                }}
                isDisabled={sanitizeUrl(linkValue) === 'about:blank'}
              >
                {t`Insert`}
              </PrimaryButton>
            </HStack>
          </AlertDialogFooter>
        </AlertDialogContent>
      </AlertDialogOverlay>
    </AlertDialog>
  );
}

function getPageLink(id: string) {
  return `${window.location.origin}${window.location.pathname}?page=${id}`;
}
